import type { ReactNode } from "react";

const linkPattern = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
const inlinePattern = /(`[^`]+`|\*\*[^*]+\*\*|__[^_]+__|\*[^*]+\*|_[^_]+_)/g;

function renderInline(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of Array.from(text.matchAll(linkPattern))) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      nodes.push(...renderInlineText(text.slice(lastIndex, matchIndex)));
    }

    const [, label, url] = match;
    nodes.push(
      <a key={`link-${matchIndex}`} href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    );
    lastIndex = matchIndex + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(...renderInlineText(text.slice(lastIndex)));
  }

  return nodes;
}

function renderInlineText(text: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;

  for (const match of Array.from(text.matchAll(inlinePattern))) {
    const matchIndex = match.index ?? 0;
    if (matchIndex > lastIndex) {
      nodes.push(text.slice(lastIndex, matchIndex));
    }

    const token = match[0];
    const content = token
      .replace(/^(`|\*\*|__|\*|_)/, "")
      .replace(/(`|\*\*|__|\*|_)$/, "");

    if (token.startsWith("`")) {
      nodes.push(<code key={`code-${matchIndex}`}>{content}</code>);
    } else if (token.startsWith("**") || token.startsWith("__")) {
      nodes.push(<strong key={`strong-${matchIndex}`}>{content}</strong>);
    } else {
      nodes.push(<em key={`em-${matchIndex}`}>{content}</em>);
    }

    lastIndex = matchIndex + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

type MarkdownBlock =
  | { type: "code"; content: string; language?: string }
  | { type: "list"; items: string[] }
  | { type: "heading"; level: 1 | 2 | 3; content: string }
  | { type: "paragraph"; content: string };

function parseBlocks(markdown: string): MarkdownBlock[] {
  const blocks: MarkdownBlock[] = [];
  const lines = markdown.split(/\r?\n/);
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const codeFence = line.match(/^```\s*([\w-]+)?\s*$/);
    if (codeFence) {
      const language = codeFence[1];
      const codeLines: string[] = [];
      index += 1;

      while (index < lines.length && !/^```\s*$/.test(lines[index])) {
        codeLines.push(lines[index]);
        index += 1;
      }

      blocks.push({ type: "code", content: codeLines.join("\n"), language });
      index += index < lines.length ? 1 : 0;
      continue;
    }

    const heading = line.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      blocks.push({
        type: "heading",
        level: heading[1].length as 1 | 2 | 3,
        content: heading[2],
      });
      index += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*[-*]\s+/, ""));
        index += 1;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    const paragraphLines = [line];
    index += 1;

    while (
      index < lines.length &&
      lines[index].trim() &&
      !/^```/.test(lines[index]) &&
      !/^(#{1,3})\s+/.test(lines[index]) &&
      !/^\s*[-*]\s+/.test(lines[index])
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }

    blocks.push({ type: "paragraph", content: paragraphLines.join("\n") });
  }

  return blocks;
}

export function MarkdownMessage({ children }: { children: string }) {
  const blocks = parseBlocks(children);

  return (
    <div className="space-y-3">
      {blocks.map((block, index) => {
        if (block.type === "code") {
          return (
            <pre
              key={index}
              className="overflow-x-auto rounded-md bg-background/60 p-3 text-xs"
            >
              {block.language && (
                <div className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">
                  {block.language}
                </div>
              )}
              <code>{block.content}</code>
            </pre>
          );
        }

        if (block.type === "list") {
          return (
            <ul key={index} className="list-disc space-y-1 pl-5">
              {block.items.map((item, itemIndex) => (
                <li key={itemIndex}>{renderInline(item)}</li>
              ))}
            </ul>
          );
        }

        if (block.type === "heading") {
          const Tag = `h${block.level}` as const;
          return <Tag key={index}>{renderInline(block.content)}</Tag>;
        }

        return (
          <p key={index} className="whitespace-pre-wrap">
            {renderInline(block.content)}
          </p>
        );
      })}
    </div>
  );
}
